import ValibotCommon from "@shared-schemas/common";
import * as v from "valibot";

type GenerateSchemaParams = Pick<
	Parameters<typeof ValibotCommon.getUuidSchema_V2>[0],
	"expected_format" | "convert_format"
>;

// ─── ENUMS ──────────────────────────────────────────────────────────────────
export const DAYS_OF_MONTH = [
	1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22,
	23, 24, 25, 26, 27, 28, 29, 30, 31,
];
export const DAYS_OF_WEEK = [
	"monday",
	"tuesday",
	"wednesday",
	"thursday",
	"friday",
	"saturday",
	"sunday",
] as const;

export const MONTHS_OF_YEAR = [
	"january",
	"february",
	"march",
	"april",
	"may",
	"june",
	"july",
	"august",
	"september",
	"october",
	"november",
	"december",
] as const;

export type DayOfWeek = (typeof DAYS_OF_WEEK)[number];
export type DayOfMonth = (typeof DAYS_OF_MONTH)[number];
export type MonthOfYear = (typeof MONTHS_OF_YEAR)[number];

// How a signal operates, as a controller presents it: 'coordinated' is coordinated-actuated,
// 'fixed-time' serves every split in full, and 'actuated' is free running.
export const OPERATING_MODES = ['coordinated', 'fixed-time', 'actuated'] as const

export type OperatingMode = (typeof OPERATING_MODES)[number]

export const DEFAULT_OPERATING_MODE: OperatingMode = 'coordinated'

const TIME_REGEX = /^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/

// ─── TIME OF DAYS ───────────────────────────────────────────────────────────

export const getOptimusTimeOfDaySchema = (params: GenerateSchemaParams) => {
	return v.object({
		time_of_day_id: ValibotCommon.getUuidSchema_V2({
			...params,
			error_field_name: "time_of_day_id",
		}),
		tod_group_id: ValibotCommon.getUuidSchema_V2({
			...params,
			error_field_name: "tod_group_id",
		}),
		signal_id: ValibotCommon.getUuidSchema_V2({
			...params,
			error_field_name: "signal_id",
		}),
		name: ValibotCommon.getNonEmptyStringSchema({ error_field_name: "name" }),
		local_start_time: v.pipe(
			v.string(),
			v.regex(TIME_REGEX, "local_start_time must be HH:MM or HH:MM:SS"),
		),
		local_end_time: v.pipe(
			v.string(),
			v.regex(TIME_REGEX, "local_end_time must be HH:MM or HH:MM:SS"),
		),
		coordination_plan_number: v.nullable(v.string()),
		phase_plan_number: v.nullable(v.string()),
		sequence_plan_number: v.nullable(v.string()),
		controller_plan_number: v.nullable(v.string()),
	});
};

// ─── TOD GROUPS ─────────────────────────────────────────────────────────────

export const getOptimusTodGroupSchema = (params: GenerateSchemaParams) => {
	return v.object({
		tod_group_id: ValibotCommon.getUuidSchema_V2({
			...params,
			error_field_name: "tod_group_id",
		}),
		signal_id: ValibotCommon.getUuidSchema_V2({
			...params,
			error_field_name: "signal_id",
		}),
		name: ValibotCommon.getNonEmptyStringSchema({ error_field_name: "name" }),
		plan_start_date: v.pipe(v.string(), v.isoTimestamp()),
		plan_number: ValibotCommon.getNonEmptyStringSchema({
			error_field_name: "plan_number",
		}),
		days_of_week: v.array(v.picklist(DAYS_OF_WEEK)),
		months: v.array(v.picklist(MONTHS_OF_YEAR)),
		days_of_month: v.array(v.picklist(DAYS_OF_MONTH)),
		time_of_days: v.array(getOptimusTimeOfDaySchema(params)),
	});
};

// ─── COORDINATION SETTINGS ──────────────────────────────────────────────────

export const tms_coordination_settings_data = v.object({
	cycle: v.number(),
	offset: v.number(),
	// Rows written before this key existed — or by any writer outside atlas-ui — fall back to the
	// default, so a single un-backfilled row can never fail the whole network-config parse.
	mode: v.optional(v.picklist(OPERATING_MODES), DEFAULT_OPERATING_MODE),
	splits: v.object({
		"1": v.number(),
		"2": v.number(),
		"3": v.number(),
		"4": v.number(),
		"5": v.number(),
		"6": v.number(),
		"7": v.number(),
		"8": v.number(),
		"9": v.number(),
		"10": v.number(),
		"11": v.number(),
		"12": v.number(),
		"13": v.number(),
		"14": v.number(),
		"15": v.number(),
		"16": v.number(),
	}),
});

export const getOptimusCoordinationSettingsSchema = (
	params: GenerateSchemaParams,
) => {
	return v.object({
		coordination_settings_id: ValibotCommon.getUuidSchema_V2({
			...params,
			error_field_name: "coordination_settings_id",
		}),
		signal_id: ValibotCommon.getUuidSchema_V2({
			...params,
			error_field_name: "signal_id",
		}),
		plan_number: ValibotCommon.getNonEmptyStringSchema({
			error_field_name: "plan_number",
		}),
		plan_start_date: v.pipe(v.string(), v.isoTimestamp()),
		coordination_settings_data: tms_coordination_settings_data,
	});
};

// ─── PHASE SETTINGS ─────────────────────────────────────────────────────────

export const tms_phase_settings_data = v.record(
	v.string(),
	v.object({
		enabled: v.boolean(),
		maxSplit: v.nullable(v.number()),
		minSplit: v.nullable(v.number()),
		vehicleExt: v.nullable(v.number()),
		coordinated: v.boolean(),
		minimumGreen: v.nullable(v.number()),
		redClearance: v.nullable(v.number()),
		allowLeftTurn: v.nullable(v.boolean()),
		minPedWalkTime: v.nullable(v.number()),
		minPedClearance: v.nullable(v.number()),
		yellowClearance: v.nullable(v.number()),
		pedestrianPhase: v.optional(v.boolean()),
		dummyPhase: v.optional(v.boolean()),
	}),
);

export const getOptimusPhaseSettingsSchema = (params: GenerateSchemaParams) => {
	return v.object({
		phase_settings_id: ValibotCommon.getUuidSchema_V2({
			...params,
			error_field_name: "phase_settings_id",
		}),
		signal_id: ValibotCommon.getUuidSchema_V2({
			...params,
			error_field_name: "signal_id",
		}),
		plan_number: ValibotCommon.getNonEmptyStringSchema({
			error_field_name: "plan_number",
		}),
		plan_start_date: v.pipe(v.string(), v.isoTimestamp()),
		phase_settings_data: tms_phase_settings_data,
	});
};

// ─── CONTROLLER SETTINGS ────────────────────────────────────────────────────
export const tms_controller_settings_data = v.object({
	// TODO: convert to picklist
	forceOffMode: v.optional(v.nullable(v.string())),
	offsetReferencePoint: v.optional(v.nullable(v.string())),
});

export const getOptimusControllerSettingsSchema = (
	params: GenerateSchemaParams,
) => {
	return v.object({
		controller_settings_id: ValibotCommon.getUuidSchema_V2({
			...params,
			error_field_name: "controller_settings_id",
		}),
		signal_id: ValibotCommon.getUuidSchema_V2({
			...params,
			error_field_name: "signal_id",
		}),
		plan_number: ValibotCommon.getNonEmptyStringSchema({
			error_field_name: "plan_number",
		}),
		plan_start_date: v.pipe(v.string(), v.isoTimestamp()),
		controller_settings_data: tms_controller_settings_data,
	});
};

// ─── SEQUENCES ──────────────────────────────────────────────────────────────
const tms_phase_slot = v.object({
	type: v.literal("phase"),
	// Phase number 1–16. 0 means "empty"/unassigned, matching the current UI.
	phase: v.pipe(v.number(), v.integer(), v.minValue(0), v.maxValue(16)),
});

const tms_barrier_slot = v.object({
	type: v.literal("barrier"),
});

const tms_ring_slot = v.variant("type", [tms_phase_slot, tms_barrier_slot]);

// A ring is just its ordered slots. The array index is the ring number, so a
// sequence can have one or many rings with no fixed ring_1..ring_4 cap.
const tms_sequence_ring = v.array(tms_ring_slot);

export const tms_sequence_data = v.pipe(
	v.object({
		rings: v.array(tms_sequence_ring),
	}),
	// Total assigned phases across every ring must not exceed 16.
	v.check((data) => {
		const total_phases = data.rings
			.flat()
			.filter((slot) => slot.type === "phase" && slot.phase !== 0).length;
		return total_phases <= 16;
	}, "A sequence may have at most 16 phases in total"),
);

export const getOptimusSequenceSchema = (params: GenerateSchemaParams) => {
	return v.object({
		sequence_id: ValibotCommon.getUuidSchema_V2({
			...params,
			error_field_name: "sequence_id",
		}),
		signal_id: ValibotCommon.getUuidSchema_V2({
			...params,
			error_field_name: "signal_id",
		}),
		plan_number: ValibotCommon.getNonEmptyStringSchema({
			error_field_name: "plan_number",
		}),
		plan_start_date: v.pipe(v.string(), v.isoTimestamp()),
		sequence_data: tms_sequence_data,
	});
};

// ─── PHASE MAPPINGS ─────────────────────────────────────────────────────────

export const getTmsAtomicMovementSchema = (params: GenerateSchemaParams) =>
	v.object({
		name: v.optional(v.string()),
		type: v.optional(v.string()),
		edge_connection_id: ValibotCommon.getUuidSchema_V2({
			...params,
			error_field_name: "edge_connection_id",
		}),
		priority: v.optional(v.string()),
		movement_type: v.optional(v.nullable(v.string())),
		is_permissive: v.optional(v.boolean()),
		is_rtor: v.optional(v.boolean()),
		overlap_name: v.optional(v.nullable(v.string())),
	});

export const getTmsPhaseMappingItemSchema = (params: GenerateSchemaParams) =>
	// TODO: Make ID not optional at some point
	v.object({
		id: v.optional(v.string()),
		key: v.optional(v.string()),
		name: v.optional(v.string()),
		number: v.number(),
		atomic_movements: v.array(getTmsAtomicMovementSchema(params)),
		priority: v.optional(v.string()),
	});

export const getOptimusPhaseMappingSchema = (params: GenerateSchemaParams) => {
	return v.object({
		phase_mapping_id: ValibotCommon.getUuidSchema_V2({
			...params,
			error_field_name: "phase_mapping_id",
		}),
		signal_id: ValibotCommon.getUuidSchema_V2({
			...params,
			error_field_name: "signal_id",
		}),
		plan_number: ValibotCommon.getNonEmptyStringSchema({
			error_field_name: "plan_number",
		}),
		plan_start_date: v.pipe(v.string(), v.isoTimestamp()),
		phase_mapping: v.array(getTmsPhaseMappingItemSchema(params)),
	});
};

// ─── STAGED WRITE PREVIEW ───────────────────────────────────────────────────

// The shape previewPendingWrite() returns: every value a staged TMS import would write, read off
// the finalized config. Section values stay loosely typed because each section contributes its own
// *_data object, and a section gaining a field must show up without this schema being touched.
export const optimus_staged_write_preview_schema = v.object({
	sections: v.array(
		v.object({
			section: v.string(),
			replaces_wholesale: v.optional(v.boolean()),
			plans: v.array(
				v.object({
					plan_number: v.string(),
					values: v.record(v.string(), v.unknown()),
				}),
			),
		}),
	),
	tod_groups: v.array(
		v.object({
			name: v.string(),
			plan_number: v.string(),
			days_of_week: v.array(v.picklist(DAYS_OF_WEEK)),
			months: v.optional(v.array(v.picklist(MONTHS_OF_YEAR))),
			windows: v.array(
				v.object({
					name: v.string(),
					start: v.string(),
					end: v.string(),
					coordination_plan_number: v.nullish(v.string()),
					phase_plan_number: v.nullish(v.string()),
					sequence_plan_number: v.nullish(v.string()),
					controller_plan_number: v.nullish(v.string()),
				}),
			),
		}),
	),
})

export type OptimusStagedWritePreview = v.InferOutput<typeof optimus_staged_write_preview_schema>

export const getNetworkOptimusConfig = (params: GenerateSchemaParams) => {
	return v.array(
		v.object({
			signal_id: ValibotCommon.getUuidSchema_V2({
				...params,
				error_field_name: "signal_id",
			}),
			controller_settings: v.array(getOptimusControllerSettingsSchema(params)),
			phase_settings: v.array(getOptimusPhaseSettingsSchema(params)),
			coordination_settings: v.array(
				getOptimusCoordinationSettingsSchema(params),
			),
			tod_groups: v.array(getOptimusTodGroupSchema(params)),
			phase_mapping: v.array(getOptimusPhaseMappingSchema(params)),
			sequences: v.array(getOptimusSequenceSchema(params)),
		}),
	);
};
